<template>
  <q-splitter
    v-if="isEnabled"
    v-model="model"
    :limits="limits"
    :unit="unit"
    :horizontal="horizontal"
    :disable="disable"
    :before-class="beforeClass"
    :after-class="afterClass"
    :separator-class="separatorClass"
    :separator-style="separatorStyle"
    :dark="dark"
    :class="className"
    :style="style"
    @update:model-value="handleUpdateModelValue"
  >
    <template v-if="$slots.before" #before>
      <slot name="before" />
    </template>
    <template v-if="$slots.after" #after>
      <slot name="after" />
    </template>
    <template v-if="$slots.separator" #separator>
      <slot name="separator" />
    </template>
  </q-splitter>
  <CuiSplitter
    v-else
    v-model:size="cuiSize"
    :orientation="horizontal ? 'vertical' : 'horizontal'"
    primary="start"
    :size-unit="unit"
    :min-size="limits[0]"
    :max-size="limits[1]"
    :class="className"
    :style="style"
    @update:size="handleCuiSizeUpdate"
  >
    <template #start>
      <div v-if="$slots.before" :class="beforeClass">
        <slot name="before" />
      </div>
    </template>
    <template #end>
      <div v-if="$slots.after" :class="[afterClass, 'splitterafterclass']">
        <slot name="after" />
      </div>
    </template>
    <template v-if="$slots.separator" #divider>
      <slot name="separator" />
    </template>
  </CuiSplitter>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useFeatureFlags } from "@/composables/useFeatureFlags";
import { CuiSplitter } from "@ist/commonui-components";

const { isEnabled: checkEnabled } = useFeatureFlags();
const isEnabled = checkEnabled("dtpSplitter");

type Props = {
  modelValue?: number;
  limits?: [number, number];
  unit?: "px" | "%";
  horizontal?: boolean;
  disable?: boolean;
  beforeClass?: string;
  afterClass?: string;
  separatorClass?: string;
  separatorStyle?: string | object;
  dark?: boolean;
  class?: string;
  style?: string | object;
};

const props = withDefaults(defineProps<Props>(), {
  unit: "px",
  horizontal: false,
  disable: false,
  limits: () => [10, 100]
});

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void;
}>();

const model = computed({
  get: () => props.modelValue ?? 0,
  set: (value) => emit("update:modelValue", value ?? 0)
});

const className = computed(() => props.class);

// CuiSplitter uses v-model:size for the start panel size
const cuiSize = ref<number>(props.modelValue ?? 50);

// Watch for external modelValue changes
watch(() => props.modelValue, (newValue) => {
  if (newValue !== undefined) {
    cuiSize.value = newValue;
  }
}, { immediate: true });

function handleUpdateModelValue(value: number) {
  emit("update:modelValue", value);
}

function handleCuiSizeUpdate(newSize: number) {
  // CuiSplitter emits update:size when the start panel size changes
  emit("update:modelValue", newSize);
}
</script>
